import { useMemo, useState } from 'react';
import { ChevronRight, Folder } from 'lucide-react';
import { Modal } from '../../Modal';
import { filePath, folderOf, foldersIn, nameOf, type BrowsableFile, type FileFolderRow, folderPathTitle } from '../../../lib/file-browser';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { useFileMove } from '../../../lib/use-file-move';
import { useTranslation } from '../../../lib/i18n';
import { Button, ScrollArea, Typography } from '../ui';
import { FolderBrowseList } from './FolderBrowseList';
import styles from './FolderPickerDialog.module.css';

// "Move to…": a small file-system browser showing FOLDERS ONLY, and you move the file into wherever you have
// navigated to.
//
// It replaced a flat radio list of every folder in the account. That list was fine with four folders and
// unusable with forty: it flattened the hierarchy into strings like "The Knudels / artifacts / reports", so the
// reader had to parse paths instead of recognising places, and it grew without bound. Browsing shows one level
// at a time and answers "where am I putting this" the same way the Files screen answers "where is this".
//
// Two decisions worth keeping:
//
//  - **The destination is the folder you are IN**, not a row you select. That is why the confirm button names it
//    ("Move here"): there is no second selection state to get out of sync with the breadcrumb, and a folder with
//    no subfolders is still a valid destination.
//  - **The tree spans workspaces**, because the agents are the same team in every one — a workspace organises
//    work rather than gating access. So the first level is the workspaces themselves, exactly as on the
//    cross-workspace Files page.
//
// There is no "new folder" here either: a folder exists because some file's path has that segment, so an empty
// one cannot be represented in this store at all.
export function FolderPickerDialog({
  item,
  onClose,
  onMoved,
}: {
  item: BrowsableFile;
  onClose: () => void;
  onMoved?: (folder: string) => void;
}) {
  const { t } = useTranslation();
  // The dialog reads the cross-workspace list itself: a workspace's own gallery only knows its own files, so a
  // caller-supplied list would silently shrink the tree to that one workspace — the same dialog offering
  // different answers depending on which screen opened it.
  const state = useAgentWorkspaceFiles();
  // Skills are dropped before the tree is derived: a move is a `workspaces` write and the server refuses
  // one into the skills directory (see `moveDestinations`), so offering the folder is a walk into a dead end.
  const all = (state.status === 'ready' ? state.items : []).filter((file) => file.workspaceId);
  const workspaceNames = useMemo(
    () => Object.fromEntries(all.map((file) => [file.workspaceId as string, file.workspaceName ?? ''])),
    [all],
  );

  // Start where the file already lives, so a move within one folder's neighbourhood is one tap.
  const [folder, setFolder] = useState<string>(folderOf(filePath(item)));
  const { state: moveState, move } = useFileMove();

  const folders: FileFolderRow[] = useMemo(() => foldersIn(all, folder), [all, folder]);
  const here = folderOf(filePath(item));
  // Moving a file into the folder it is already in is not a move. The rule lives in `planFileMove` too (the
  // server refuses it); the button is disabled so nobody has to discover that by failing.
  const canMove = folder !== '' && folder !== here;

  const label = (path: string) => {
    return folderPathTitle(path, workspaceNames, t);
  };

  const submit = async () => {
    if (!canMove) return;
    const ok = await move(item, folder);
    if (!ok) return;
    onMoved?.(label(folder));
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      footerSeparator
      title={t('files.move.title')}
      description={t('files.move.description', { file: nameOf(item.name) })}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={!canMove} loading={moveState === 'moving'}>
            {t('files.move.confirm')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {/* The same browsing surface the New file and Upload dialogs ask with — one place, so all three
            agree that the first level is Spaces and everything under it is folders. */}
        <FolderBrowseList
          folder={folder}
          onNavigate={setFolder}
          files={all}
          workspaceNames={workspaceNames}
          loading={state.status === 'loading'}
        />
        <Typography variant="p3" color="secondary" component="p">
          {folder === '' ? t('files.move.pickWorkspace') : t('files.move.destination', { folder: label(folder) })}
        </Typography>

        {moveState === 'error' && (
          <Typography variant="p2" component="p" role="alert" className={styles.error}>
            {t('files.move.error')}
          </Typography>
        )}
      </div>
    </Modal>
  );
}
